// Shared money/format helpers used by the payments dashboard, the
// platform-admin managed-payouts tab, and the refund modal. Keeping
// these in one place so a future formatting tweak (e.g. locale-aware
// thousands separators) doesn't need a cross-file sweep.
//
// All amounts in our payment domain are stored as integer cents to
// avoid float drift - these helpers go from cents → human string.

import { getUserLocale } from '@/lib/locale'

const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW', 'VND'])

export function formatMoneyCents(cents: number, currency: string): string {
  const cfg = getUserLocale()
  const code = String(currency || cfg.currency).toUpperCase()
  const amount = ZERO_DECIMAL_CURRENCIES.has(code) ? cents : cents / 100
  try {
    return new Intl.NumberFormat(cfg.locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2,
      maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2,
    }).format(amount)
  } catch {
    return `${code} ${amount.toFixed(2)}`
  }
}

// Stripe's balance.retrieve / payouts.list returns amounts in the
// currency's smallest unit. For zero-decimal currencies (JPY, KRW,
// VND) that smallest unit IS the major unit; we hand it off to Intl
// untouched. Same helper as formatMoneyCents - kept as a separate
// export so call sites read intent-fully.
export const formatStripeBalance = formatMoneyCents

export function bpsToPercent(bps: number): string {
  const pct = bps / 100
  return `${pct.toFixed(pct % 1 === 0 ? 0 : 2)}%`
}
